/*

--------------------------------------------------------------------------------
Custom textbox widget using some Kendo-style functionality.
Provides textbox with validation and tooltip popup icons and applying similar component status behavior
as in native Kendo UI widgets.

For tooltips and validation implementation see ori.kendo.tooltip.js
and ori.kendo.validator.js.
--------------------------------------------------------------------------------

<![CDATA[

*/

; (function ($) {

    "use strict";

    var kendo = window.kendo,
        keys = kendo.keys,
        ui = kendo.ui,
        Widget = ui.Widget,
        getCulture = kendo.getCulture,
        CHANGE = "change",
        INPUT = "k-input",
        NS = ".kendoTextbox",
        DEFAULT = "k-state-default",
        FOCUSED = "k-state-focused",
        HOVER = "k-state-hover",
        DISABLED = "disabled",
        STATEDISABLED = "k-state-disabled",
        READONLY = "readonly",
        STATEREADONLY = "k-state-readonly",
        AVATAR = "avatar",
        MOUSELEAVE = 'mouseleave' + NS,
        HOVEREVENTS = 'mouseenter' + NS + ' ' + MOUSELEAVE,
        TOUCHEVENTS = "touchend" + NS + " click" + NS,
        proxy = $.proxy;

    function setupNs(ns) {
        NS = ns || NS;
        HOVEREVENTS = "mouseenter" + NS + " " + "mouseleave" + NS;
    }

    var TextBox = Widget.extend({

        init: function (element, options) {
            var self = this,
                value,
                maxlength,
                disabled;
            Widget.fn.init.call(self, element, options);
            setupNs(self.ns);
            element = self.element
                .addClass(INPUT)
                .on("keydown" + NS, proxy(self._keydown, self))
                .on("keyup" + NS, proxy(self._keyup, self))
                .on("paste" + NS, proxy(self._paste, self))
                .on("blur" + NS, proxy(self._focusout, self))
                .attr("role", "textbox");
            maxlength = self.maxLength(element.attr('maxlength'));
            if (self.options.maxLength < 1 && maxlength > 0) {
                self.options.maxLength = maxlength;
            }
            // remember title text - needed for various description UI types
            self.options.titleText = self.options.titleText || element.attr("title");
            element.removeAttr("title");
            self._reset();
            self._wrapper();
            Widget.fn.descriptionUi.call(self);
            if (!kendo.support.mobileOS) {
                element.on("focus" + NS, proxy(self._click, self));
                self.wrapper.on("focus" + NS, proxy(self._click, self));
            }
            if (self.options.maxLength > 0) {
                element.attr('maxlength', self.options.maxLength);
            }
            value = self.options.value;
            self.value(value !== null ? value : element.val());
            disabled = element.is('[disabled]') || $(self.element).parents('fieldset').is(':disabled');
            if (disabled) {
                self.enable(false);
            } else {
                self.readonly(element.is('[readonly]'));
            }
            kendo.notify(self);
        },

        options: kendo.getUpdatedWidgetOptions({
            name: "TextBox",
            value: null,
            culture: ""
        }),

        events: [CHANGE],

        /**
         * working with enable and readonly
         */
        _editable: function (options) {
            var self = this,
                element = self.element,
                disable = options.disable,
                readonly = options.readonly,
                wrapper = self.wrapper.off(HOVEREVENTS);

            if (!readonly && !disable) {
                wrapper
                    .addClass(DEFAULT)
                    .removeClass(STATEREADONLY)
                    .removeClass(STATEDISABLED)
                    .on(HOVEREVENTS, self._toggleHover)
                    .on(TOUCHEVENTS, proxy(self._click, self));
                element
                    .prop(DISABLED, false)
                    .prop(READONLY, false);
            } else {
                if (readonly) {
                    wrapper.addClass(STATEREADONLY).removeClass(DEFAULT).removeClass(STATEDISABLED);
                }
                if (disable) {
                    wrapper.addClass(STATEDISABLED).removeClass(DEFAULT).removeClass(STATEREADONLY);
                }
                element.prop(DISABLED, disable).prop(READONLY, readonly);
            }
        },
        readonly: function (readonly) {
            this._editable({
                readonly: readonly === undefined ? true : readonly,
                disable: false
            });
        },
        enable: function (enable) {
            this._editable({
                readonly: false,
                disable: !(enable = enable === undefined ? true : enable)
            });
        },

        /**
         * Destroys the widget.
         */
        destroy: function () {
            var self = this;
            self.element
                .add(self.wrapper)
                .off(NS);
            if (self._form) self._form.off("reset", self._resetHandler);
            Widget.fn.destroy.call(self);
        },

        value: function (value) {
            var self = this;
            if (value === undefined) return self._value;
            self._update(value);
            self._old = self._value;
        },

        focus: function (event) {
            this._focusin(event);
        },

        maxLength: function (value) {
            return this._option('maxLength', value);
        },

        forceInputUppercase: function (target) {
            if (!target) {
                return;
            }
            var start = target.selectionStart,
                end = target.selectionEnd,
                locale = getCulture(this.options.culture);
            target.value = target.value.toLocaleUpperCase(locale.name);
            target.setSelectionRange(start, end);
        },

        _blur: function () {
            var self = this;
            self._change(self.element.val());
        },

        _click: function (event) {
            var self = this;
            clearTimeout(self._focusing);
            self._focusing = setTimeout(function () {
                self._focusin(event);
            });
        },

        _change: function (value) {
            var self = this;
            self._update(value);
            value = self._value;
            if (self._old !== value) {
                self._old = value;
                self.trigger(CHANGE);
                // trigger the DOM change event so any subscriber gets notified
                self.element.trigger(CHANGE);
            }
        },

        _culture: function (culture) {
            return culture || getCulture(this.options.culture);
        },

        _focusin: function (event) {
            var self = this;
            self.info("_focusin(), event : %o", event);
            self.wrapper.addClass(FOCUSED);
            self.element[0].focus();
        },

        _focusout: function (event) {
            var self = this;
            self.info("_focusout(), event : %o", event);
            clearTimeout(self._focusing);
            self.wrapper.removeClass(FOCUSED);
            self._blur();
        },

        _keydown: function (event) {
            var self = this,
                key = event.keyCode,
                val = self.element.val();
            self.info("key down - key : %s, value : '%s'", key, val);
            if (key === keys.ENTER) self._change(val);
        },

        _keyup: function (event) {
            var self = this,
                val = self.element.val(),
                uppercasemode = self.options.upperCaseMode;
            if (uppercasemode && val.length > 0) {
                self.forceInputUppercase(event.target);
            }
        },

        _paste: function (event) {
            var self = this,
                element = event.target,
                value = element.value;
            setTimeout(function () {
                if (element.value === null) self._update(value);
            });
        },

        _option: function (option, value) {
            var self = this,
                options = self.options;
            if (value === undefined) return options[option];
            options[option] = value;
            self.element.attr("aria-value" + option, options[option]);
        },

        _toggleHover: function (event) {
            $(event.currentTarget).toggleClass(HOVER, event.type === "mouseenter");
        },

        _update: function (value) {
            var self = this;
            self._value = value;
            self.element.val(value.toString())
        },

        _wrapper: function () {
            var self = this,
                $element = self.element,
                DOMelement = $element[0],
                $wrapper;
            $wrapper = $element.parent();
            $element.removeClass(AVATAR);
            if (!$wrapper.is("span.k-widget"))
                $wrapper = $element.wrap("<span />").parent();
            // aria
            $wrapper
                .attr("tabindex", -1)
                .attr("role", "presentation");
            // styling
            $wrapper[0].style.cssText = DOMelement.style.cssText;
            self.wrapper = $wrapper
                .addClass("k-widget k-textbox k-header")
                .addClass(DOMelement.className)
                .removeClass("k-input");
        },

        _reset: function () {
            var self = this,
                $element = self.element,
                $form = $element.closest("form");
            if ($form[0]) {
                self._resetHandler = function () {
                    setTimeout(function () {
                        self.value($element[0].value);
                    });
                };
                self._form = $form.on("reset", self._resetHandler);
            }
        }

    });

    ui.plugin(TextBox);

})(window.kendo.jQuery);

/* ]]> */